import {RequestInfo} from "@khan/shared-core";

import {parseUrl} from "./parse-url.ts";
import {ABSOLUTE_REGEX, KA_DOMAIN_REGEX} from "./regexp.ts";
import {toAbsoluteUrl} from "./to-absolute-url.ts";

/**
 * Determines whether or not the specified URL points to a Khan Academy
 * property.
 *
 * Absolute URLs are expected to be well-formed (include scheme + subdomain).
 * Relative URLs are considered safe and owned by Khan Academy.
 *
 * @see lib/url_util.py#is_khanacademy_url
 *
 * @param {string} url The URL to be validated against
 * @returns {string} Whether the specified URL points to a Khan Academy
 * property.
 */
export const isKhanAcademyUrl = (url: string): boolean => {
    // Remove whitespaces from both left and right.
    url = url.trim();

    // Chrome treats backslashes in urls the same as forward slashes but other
    // browsers do not. Also we never generate such urls. To be safe, reject.
    if (url.includes("\\")) {
        return false;
    }

    // Chrome ignores all consecutive slashes in the scheme after the second;
    // urlparse does not. To be safe, reject such urls. Perform this check after
    // checking for mobile schemes because those have no slash restrictions.
    if (url.includes("///")) {
        return false;
    }

    if (!ABSOLUTE_REGEX.test(url)) {
        // Relative URLs might be malformed (either deliberately or by
        // accident). Note that scheme-less absolute URLs look like relative
        // URLs to urlsplit: urlparse.urlsplit("www.example.com").path ==
        // "www.example.com"
        if (!url.startsWith("/")) {
            return false;
        }

        // If we are not on a dev server, then append the origin to allow the
        // URL constructor to be used.
        url = toAbsoluteUrl(url);
    }

    let address;

    try {
        address = parseUrl(url);
    } catch (e: any) {
        // invalid URL
        return false;
    }

    // In case the URL is correctly formatted, now extract the URL parts.
    const {hostname, protocol, username} = address;

    // If this is an absolute path, make sure we're only allowing HTTPS or HTTP
    // schemes.
    if (protocol !== "https:" && protocol !== "http:") {
        return false;
    }

    // We do not allow usernames in the url.  It's too easy to get the hostname
    // part wrong when those happen, and we don't use them.
    if (username) {
        return false;
    }

    // If we pass all the validations, then we can determine this URL is being
    // handled in a dev server (e.g. localhost).
    if (RequestInfo.IS_DEV_SERVER) {
        return true;
    }

    return KA_DOMAIN_REGEX.test(hostname);
};
